import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import { useExamResults, useSubjectPerformance } from "@/hooks/useExams";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";

// Tailwind sınıflarını chart'ta kullanılabilir oklch renklerine çevirir
const SUBJECT_COLORS: Record<string, string> = {
  "bg-primary-500": "oklch(0.55 0.0996 221)",
  "bg-primary-400": "oklch(0.70 0.115 211)",
  "bg-accent-500": "oklch(0.72 0.12 211)",
  "bg-accent-400": "oklch(0.75 0.115 210)",
  "bg-secondary-500": "oklch(0.60 0.055 215)",
  "bg-secondary-400": "oklch(0.71 0.05 214)",
};

const PRIMARY = "oklch(0.55 0.0996 221)";
const ACCENT = "oklch(0.72 0.12 211)";
const SECONDARY = "oklch(0.60 0.055 215)";
const TICK = "oklch(0.35 0.032 231)";
const GRID = "oklch(0.92 0.02 208)";

const tooltipStyle = {
  borderRadius: 10,
  border: "1px solid oklch(0.92 0.02 208)",
  background: "oklch(0.985 0.004 210)",
  boxShadow: "0 8px 24px oklch(0.19 0.01 205 / 0.08)",
  fontSize: 12,
};

export default function BasariAnalizim() {
  const { data: examResults, loading: l1, error: e1, reload: r1 } = useExamResults();
  const { data: subjectPerformance, loading: l2, error: e2, reload: r2 } = useSubjectPerformance();

  if (l1 || l2) return <LoadingState />;
  if (e1) return <ErrorState message={e1} onRetry={r1} />;
  if (e2) return <ErrorState message={e2} onRetry={r2} />;

  const results = examResults ?? [];
  const subjects = subjectPerformance ?? [];

  const avgNet = results.length > 0
    ? (results.reduce((sum, e) => sum + e.net, 0) / results.length).toFixed(1)
    : "0.0";

  const bestRank = results.reduce((best, e) => {
    const rank = parseInt(e.rank.split("/")[0], 10);
    return !Number.isNaN(rank) && rank < best ? rank : best;
  }, Infinity);

  // En eskiden en yeniye net ilerlemesi
  const netProgression = [...results].reverse().map((e) => ({
    label: e.name.replace("5. Sınıf Deneme Sınavı - ", "Deneme "),
    net: e.net,
  }));

  // Ders bazlı başarı -> yüzde + gerçek renk
  const subjectData = subjects.map((s) => ({
    subject: s.subject,
    yuzde: s.maxNet > 0 ? Math.round((s.net / s.maxNet) * 100) : 0,
    net: s.net,
    maxNet: s.maxNet,
    fill: SUBJECT_COLORS[s.color] ?? PRIMARY,
  }));

  // Son denemenin doğru/yanlış/boş dağılımı
  const latest = results[0];
  const breakdown = latest
    ? [
        { name: "Doğru", value: latest.correct, color: PRIMARY },
        { name: "Yanlış", value: latest.wrong, color: ACCENT },
        { name: "Boş", value: latest.blank, color: SECONDARY },
      ]
    : [];

  const stats = [
    { label: "Toplam Deneme", value: String(results.length), icon: "ri-file-text-line" },
    { label: "Ortalama Net", value: avgNet, icon: "ri-line-chart-line" },
    { label: "En İyi Sıralama", value: Number.isFinite(bestRank) ? `${bestRank}.` : "—", icon: "ri-trophy-line" },
    { label: "Soru Sayısı", value: results.length > 0 ? String(results[0].totalQuestions) : "—", icon: "ri-list-check-2" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">Başarı Analizim</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-1">
            Net değişimin, ders bazlı başarın ve genel durumun.
          </p>
        </div>
      </div>

      {/* Özet kartlar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-background-50 rounded-xl border border-background-200 p-5">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className={`${stat.icon} text-lg`} aria-hidden="true"></i>
            </span>
            <p className="mt-3 text-2xl font-bold text-foreground-950">{stat.value}</p>
            <p className="text-sm text-foreground-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Net ilerleme - alan grafiği */}
      <div className="bg-background-50 rounded-xl border border-background-200 p-5">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading font-semibold text-base text-foreground-950">Net İlerlemesi</h2>
          <span className="text-xs text-foreground-500 whitespace-nowrap">denemeler boyunca net</span>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={netProgression} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="netFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={PRIMARY} stopOpacity={0.32} />
                  <stop offset="95%" stopColor={PRIMARY} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: TICK }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: TICK }}
                tickLine={false}
                axisLine={false}
                domain={[0, "auto"]}
                allowDecimals={false}
              />
              <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: GRID }} />
              <Area
                type="monotone"
                dataKey="net"
                stroke={PRIMARY}
                strokeWidth={2.5}
                fill="url(#netFill)"
                dot={{ r: 3, fill: PRIMARY, strokeWidth: 0 }}
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ders bazlı başarı - yatay çubuk grafiği */}
        <div className="bg-background-50 rounded-xl border border-background-200 p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading font-semibold text-base text-foreground-950">Ders Bazlı Başarı</h2>
            <span className="text-xs text-foreground-500 whitespace-nowrap">başarı yüzdesi</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={subjectData}
                layout="vertical"
                margin={{ top: 0, right: 24, left: 8, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  tick={{ fontSize: 11, fill: TICK }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `%${v}`}
                />
                <YAxis
                  type="category"
                  dataKey="subject"
                  width={90}
                  tick={{ fontSize: 12, fill: TICK }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  cursor={{ fill: "oklch(0.964 0.018 209)" }}
                  formatter={(value: number, _name, item) => [
                    `${item?.payload.net} / ${item?.payload.maxNet} net`,
                    item?.payload.subject,
                  ]}
                />
                <Bar dataKey="yuzde" radius={[0, 8, 8, 0]} barSize={20}>
                  {subjectData.map((entry) => (
                    <Cell key={entry.subject} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Son deneme dağılımı - pasta grafiği */}
        <div className="bg-background-50 rounded-xl border border-background-200 p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading font-semibold text-base text-foreground-950">Son Deneme Dağılımı</h2>
            <span className="text-xs text-foreground-500 whitespace-nowrap">doğru / yanlış / boş</span>
          </div>
          {breakdown.length > 0 ? (
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="h-52 w-52 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={breakdown}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      strokeWidth={0}
                    >
                      {breakdown.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-3 w-full">
                {breakdown.map((b) => (
                  <li key={b.name} className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm text-foreground-700">
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: b.color }}
                        aria-hidden="true"
                      />
                      {b.name}
                    </span>
                    <span className="text-sm font-semibold text-foreground-950">{b.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-foreground-500">Henüz deneme sonucu yok.</p>
          )}
        </div>
      </div>
    </div>
  );
}